import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Bot, Loader2, Mic, Shield, AlertTriangle,
    Eye, Volume2, Wifi, WifiOff, Brain, Clock, CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import * as apiClient from '../services/apiClient';
import AudioRecorder from '../components/AudioRecorder';
import CodingIDE from '../components/CodingIDE';
import TerminatedPage from './TerminatedPage';
import { API_CONFIG } from '../config/config.js';
import { Button } from '@/components/ui/button';

/* ─────────────────────────── helpers ─────────────────────────────────────── */
function getSessionKey(sessionId, key) { return `${key}-${sessionId}`; }

const GAZE_LABELS = { center: 'On screen', left: 'Looking left', right: 'Looking right', down: 'Looking down', up: 'Looking up', unknown: 'Unknown' };
const GAZE_OK = (d) => d === 'center' || d === 'unknown';

/* ─────────────────────────── Client-side proctoring hook (fallback) ──────── */
// Used only when the backend proctor service is unreachable (proctoringDegraded).
// Tracks face count and gaze for display only — no warnings, no termination.
// Tab-switch termination is always handled by the frontend regardless.
function useClientProctoring({ videoRef, enabled }) {
    const [state, setState] = useState({ faceCount: 1, gazeDirection: 'center', gazeOnScreenPct: 100 });
    const gazeFramesRef = useRef({ total: 0, onScreen: 0 });
    const detectorRef   = useRef(null);
    const initRef       = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        let iv;

        const init = async () => {
            if (initRef.current) return;
            initRef.current = true;
            try {
                if (typeof FaceDetector !== 'undefined') {
                    detectorRef.current = new FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
                }
            } catch { detectorRef.current = null; }
        };

        const check = async () => {
            const video = videoRef.current;
            if (!video?.videoWidth || !detectorRef.current) return;
            try {
                const faces = await detectorRef.current.detect(video);
                if (faces.length === 0) {
                    setState(s => ({ ...s, faceCount: 0, gazeDirection: 'unknown' }));
                    return;
                }
                const face = faces[0];
                const cx = (face.boundingBox.x + face.boundingBox.width / 2) / video.videoWidth;
                const cy = (face.boundingBox.y + face.boundingBox.height / 2) / video.videoHeight;
                let gaze = 'center';
                if (cx < 0.28) gaze = 'right';
                else if (cx > 0.72) gaze = 'left';
                else if (cy < 0.2 || cy > 0.75) gaze = 'down';
                gazeFramesRef.current.total += 1;
                if (gaze === 'center') gazeFramesRef.current.onScreen += 1;
                const pct = Math.round(100 * gazeFramesRef.current.onScreen / gazeFramesRef.current.total);
                setState({ faceCount: faces.length, gazeDirection: gaze, gazeOnScreenPct: pct });
            } catch { detectorRef.current = null; }
        };

        init().then(() => { iv = setInterval(check, 2500); });
        return () => clearInterval(iv);
    }, [enabled]);

    return state;
}

/* ─────────────────────────── Voice monitor + dual voice detection ─────────── */
function useVoiceMonitor(stream, enabled = true) {
    const [level, setLevel] = useState(0);
    const [events, setEvents] = useState([]);
    const [dualVoiceDetected, setDualVoiceDetected] = useState(false);
    const dualFlagCountRef      = useRef(0);    // cumulative episode count for integrity score
    const dualEpisodeActiveRef  = useRef(false); // are we currently inside a dual-voice episode?
    // Sustain gate: require N consecutive ~60fps frames of dual detection before
    // flipping dualVoiceDetected to true. Prevents momentary noise spikes counting as episodes.
    const dualSustainFramesRef  = useRef(0);
    const DUAL_SUSTAIN_THRESHOLD = 25; // ~400 ms at 60 fps

    const analyserRef     = useRef(null);
    const dualAnalyserRef = useRef(null);
    const ctxRef          = useRef(null);
    const rafRef          = useRef(null);
    const quietTimerRef   = useRef(null);
    const noisyTimerRef   = useRef(null);
    const quietStartRef   = useRef(null);

    useEffect(() => {
        if (!stream || !enabled) return;
        try {
            ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const src = ctxRef.current.createMediaStreamSource(stream);

            // ── Primary analyser — level meter ────────────────────────────
            const analyser = ctxRef.current.createAnalyser();
            analyser.fftSize = 256;
            src.connect(analyser);
            analyserRef.current = analyser;
            const buf = new Uint8Array(analyser.frequencyBinCount);

            // ── Secondary analyser — dual-voice detection (FFT peaks) ─────
            // Two distinct fundamental frequencies in the 80-300 Hz voice band
            // appearing simultaneously suggest two speakers.
            const dualAnalyser = ctxRef.current.createAnalyser();
            dualAnalyser.fftSize = 2048;
            dualAnalyser.smoothingTimeConstant = 0.8;
            src.connect(dualAnalyser);
            dualAnalyserRef.current = dualAnalyser;
            const dualBuf = new Float32Array(dualAnalyser.frequencyBinCount);
            const sampleRate = ctxRef.current.sampleRate;
            const hzPerBin   = sampleRate / dualAnalyser.fftSize;
            const loIdx      = Math.max(1, Math.floor(80  / hzPerBin));
            const hiIdx      = Math.ceil(300 / hzPerBin);

            const poll = () => {
                // ── Level ─────────────────────────────────────────────────
                analyser.getByteFrequencyData(buf);
                const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
                const pct = Math.min(100, Math.round(avg * 2.5));
                setLevel(pct);

                // ── Dual-voice (only worth checking when audio is active) ─
                if (pct > 30) {
                    dualAnalyser.getFloatFrequencyData(dualBuf);
                    const slice = dualBuf.slice(loIdx, hiIdx + 1);
                    // Collect local spectral peaks above the RAISED energy floor.
                    // Old thresholds (-40/-45 dBFS, 2-bin gap) were too sensitive:
                    //   • -45 dBFS is barely above typical laptop microphone noise floor
                    //   • 2 bins ≈ 43 Hz — a single voice's harmonics are this close
                    // New thresholds require genuinely strong, well-separated peaks so
                    // a single speaker's harmonics or room reverb don't trigger a flag.
                    const peaks = [];
                    for (let i = 1; i < slice.length - 1; i++) {
                        if (slice[i] > -28 && slice[i] > slice[i - 1] && slice[i] > slice[i + 1]) {
                            peaks.push({ idx: i, energy: slice[i] });
                        }
                    }
                    peaks.sort((a, b) => b.energy - a.energy);
                    // Two dominant peaks that are:
                    //   • Both above −22 dBFS (primary) / −28 dBFS (secondary) — strong signal
                    //   • At least 6 bins apart ≈ 129 Hz — eliminates same-speaker harmonics
                    const rawDual = peaks.length >= 2 &&
                        peaks[0].energy > -22 &&
                        peaks[1].energy > -28 &&
                        Math.abs(peaks[0].idx - peaks[1].idx) >= 6;

                    // Sustain gate: only declare dual voice after DUAL_SUSTAIN_THRESHOLD
                    // consecutive positive frames (~400 ms). Resets immediately on any
                    // non-dual frame so a brief noise spike never completes the count.
                    if (rawDual) {
                        dualSustainFramesRef.current = Math.min(
                            dualSustainFramesRef.current + 1,
                            DUAL_SUSTAIN_THRESHOLD + 1
                        );
                    } else {
                        dualSustainFramesRef.current = 0;
                    }
                    const sustainedDual = dualSustainFramesRef.current >= DUAL_SUSTAIN_THRESHOLD;
                    setDualVoiceDetected(sustainedDual);

                    // Count per episode: only increment on the rising edge (first frame that
                    // crosses the sustain threshold), not on every subsequent frame.
                    if (sustainedDual && !dualEpisodeActiveRef.current) {
                        dualFlagCountRef.current += 1;
                        dualEpisodeActiveRef.current = true;
                    } else if (!sustainedDual) {
                        dualEpisodeActiveRef.current = false;
                    }
                } else {
                    dualSustainFramesRef.current = 0;
                    setDualVoiceDetected(false);
                    dualEpisodeActiveRef.current = false;
                }

                // ── High noise event (>70% for >4 s) ─────────────────────
                if (pct > 70) {
                    if (!noisyTimerRef.current) noisyTimerRef.current = setTimeout(() => {
                        setEvents(e => [...e.slice(-19), { type: 'high_noise', level: pct, time: Date.now() }]);
                        noisyTimerRef.current = null;
                    }, 4000);
                } else {
                    if (noisyTimerRef.current) { clearTimeout(noisyTimerRef.current); noisyTimerRef.current = null; }
                }

                // ── Prolonged silence (< 5% for >6 s) ────────────────────
                if (pct < 5) {
                    if (!quietStartRef.current) quietStartRef.current = Date.now();
                    else if (Date.now() - quietStartRef.current > 6000 && !quietTimerRef.current) {
                        quietTimerRef.current = setTimeout(() => {
                            setEvents(e => [...e.slice(-19), { type: 'prolonged_silence', time: Date.now() }]);
                            quietTimerRef.current = null;
                            quietStartRef.current = null;
                        }, 100);
                    }
                } else {
                    quietStartRef.current = null;
                    if (quietTimerRef.current) { clearTimeout(quietTimerRef.current); quietTimerRef.current = null; }
                }

                rafRef.current = requestAnimationFrame(poll);
            };
            rafRef.current = requestAnimationFrame(poll);
        } catch { /* AudioContext unavailable — degrade gracefully */ }

        return () => {
            cancelAnimationFrame(rafRef.current);
            clearTimeout(quietTimerRef.current);
            clearTimeout(noisyTimerRef.current);
            ctxRef.current?.close().catch(() => {});
            analyserRef.current     = null;
            dualAnalyserRef.current = null;
            ctxRef.current          = null;
        };
    }, [stream, enabled]);

    return { level, events, dualVoiceDetected, dualFlagCountRef };
}

/* ─────────────────────────── Waveform bars ───────────────────────────────── */
function WaveBar({ level, count = 20 }) {
    const heights = Array.from({ length: count }, (_, i) => {
        if (level < 2) return 4;
        const center = count / 2;
        const dist = Math.abs(i - center) / center;
        return Math.max(4, level * 0.6 * (1 - dist * 0.5) * (0.6 + Math.random() * 0.8));
    });
    return (
        <div className="flex items-center justify-center gap-[2px]" style={{ height: 32 }}>
            {heights.map((h, i) => (
                <div key={i} className={cn('w-[2.5px] rounded-full transition-all duration-75',
                    level > 2 ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-muted-foreground/20')}
                    style={{ height: `${Math.min(32, h)}px` }} />
            ))}
        </div>
    );
}

/* ─────────────────────────── Circular timer ──────────────────────────────── */
function RingTimer({ value, max }) {
    const pct = max > 0 ? Math.min(1, value / max) : 1;
    const R = 28, circ = 2 * Math.PI * R;
    const urgent = value <= 30, warning = value <= 60 && !urgent;
    const color = urgent ? 'text-destructive' : warning ? 'text-amber-500' : 'text-emerald-500';
    const stroke = urgent ? 'var(--color-destructive, #ef4444)' : warning ? '#f59e0b' : '#10b981';
    const t = `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
    return (
        <div className="relative flex items-center justify-center" style={{ width: 72, height: 72 }}>
            <svg width={72} height={72} className="-rotate-90 absolute inset-0">
                <circle cx={36} cy={36} r={R} fill="none" strokeWidth={3} className="stroke-muted/30" />
                <circle cx={36} cy={36} r={R} fill="none" strokeWidth={3} stroke={stroke} strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
                    style={{ transition: 'stroke-dashoffset .5s ease, stroke .5s ease' }} />
            </svg>
            <div className="z-10 text-center">
                <span className={cn('text-sm font-black tabular-nums', color)}>{t}</span>
            </div>
        </div>
    );
}

/* ─────────────────────────── Progress strip ──────────────────────────────── */
function QuestionProgress({ total, current }) {
    return (
        <div className="flex items-center gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
                <div key={i} className={cn('h-1 rounded-full transition-all duration-500',
                    i < current ? 'w-3 bg-emerald-500' : i === current ? 'w-5 bg-primary' : 'w-1.5 bg-muted/50')} />
            ))}
            <span className="ml-2 text-[11px] font-semibold text-muted-foreground tabular-nums">
                {current + 1}/{total}
            </span>
        </div>
    );
}

/* ─────────────────────────── Tracking status row ─────────────────────────── */
function TrackRow({ icon: Icon, label, status, value, danger }) {
    return (
        <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors',
            danger ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-card/50')}>
            <Icon className={cn('h-3.5 w-3.5 shrink-0', danger ? 'text-destructive' : 'text-muted-foreground')} />
            <span className="flex-1 font-medium text-foreground">{label}</span>
            {status !== undefined && (
                <span className={cn('h-1.5 w-1.5 rounded-full', status ? 'bg-emerald-500 animate-pulse' : 'bg-destructive')} />
            )}
            {value !== undefined && (
                <span className={cn('font-mono tabular-nums text-[10px]', danger ? 'text-destructive' : 'text-muted-foreground')}>{value}</span>
            )}
        </div>
    );
}

/* ─────────────────────────── Safety Warning Modal ────────────────────────── */
function SafetyWarningModal({ message, onDismiss }) {
    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-md rounded-2xl border border-destructive/40 bg-background p-6 shadow-2xl text-center space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mx-auto">
                    <AlertTriangle className="h-7 w-7 text-destructive" />
                </div>
                <div className="space-y-2">
                    <h2 className="text-xl font-black text-foreground">⚠ Interview Warning</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
                </div>
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
                    <p className="text-xs font-bold text-destructive leading-snug">
                        Doing this again will immediately and permanently terminate your interview.
                    </p>
                </div>
                <Button onClick={onDismiss} className="w-full h-11 font-semibold">
                    I Understand — Continue Interview
                </Button>
            </div>
        </div>
    );
}

/* ─────────────────────────── Soft toast (non-blocking) ───────────────────── */
const SOFT_TOAST_COLORS = {
    info:    'border-blue-500/30 bg-blue-950/80 text-blue-300',
    warning: 'border-amber-500/30 bg-amber-950/80 text-amber-300',
    danger:  'border-red-500/40 bg-red-950/80 text-red-300',
};

function SoftToast({ msg, type = 'info', onDismiss }) {
    return (
        <div className={cn(
            'flex items-start gap-2.5 rounded-xl border px-4 py-3 text-xs font-semibold shadow-lg',
            'animate-in fade-in slide-in-from-top-2 duration-300',
            SOFT_TOAST_COLORS[type] ?? SOFT_TOAST_COLORS.info,
        )}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="flex-1 leading-snug">{msg}</span>
            <button onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100 leading-none">✕</button>
        </div>
    );
}

/* ─────────────────────────── No-face warning banner ──────────────────────── */
function NoFaceWarningBanner({ secsLeft }) {
    return (
        <div className="z-50 flex items-center justify-center gap-2 bg-red-600 px-4 py-2.5 text-sm font-bold text-white">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
                No face detected!{' '}
                Return to camera immediately —{' '}
                interview ends in <span className="tabular-nums">{secsLeft ?? '…'}s</span>
            </span>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
const InterviewPage = ({ uniqueLink, onInterviewComplete, initialStream = null }) => {
    const [sessionData, setSessionData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [phase, setPhase] = useState('interview'); // 'interview' | 'finalizing'
    const [error, setError] = useState('');
    const [mediaStream, setMediaStream] = useState(null);
    const [terminated, setTerminated] = useState(false);
    // MCQ: tracks which option the candidate has selected for the current question.
    // Mirrored into a ref because backgroundSubmit is a useCallback that must not be
    // rebuilt on every selection — reading the state directly there captures a stale null.
    const [selectedMcqOption, setSelectedMcqOption] = useState(null);
    const selectedMcqOptionRef = useRef(null);
    const [faceCount, setFaceCount] = useState(1);
    // True only after 3 consecutive proctor polls return multiple_faces_warning.
    // Prevents single-frame Haar false positives from lighting up the UI.
    const [confirmedMultiFace, setConfirmedMultiFace] = useState(false);
    const [proctoringEventLog, setProctoringEventLog] = useState([]);
    const [terminationReason, setTerminationReason] = useState(null);
    // No-face timer warning (from backend proctor service)
    const [noFaceWarning, setNoFaceWarning] = useState(null); // { secsLeft: number } | null
    // Soft toasts — informational, non-blocking
    const [softToasts, setSoftToasts] = useState([]);
    const [displayTime, setDisplayTime] = useState(300);
    const [questionMaxTime, setQuestionMaxTime] = useState(300);
    const [tabSwitchCount, setTabSwitchCount] = useState(0);
    const [proctoringDegraded, setProctoringDegraded] = useState(false);
    const [videoHasPlayed, setVideoHasPlayed] = useState(false);
    const [gazeInfo, setGazeInfo] = useState({ direction: 'unknown', on_screen_pct: 100 });
    const [faceDetected, setFaceDetected] = useState(true);
    const [questionEntering, setQuestionEntering] = useState(false);
    const [recording, setRecording] = useState(false);
    const [bgProcessing, setBgProcessing] = useState(false); // background submit in progress
    const [safetyWarning, setSafetyWarning] = useState(null); // { msg } — shown as blocking modal
    const [submitConfirm, setSubmitConfirm] = useState(false); // pending submit confirmation
    const [joinImages, setJoinImages] = useState([]); // captured images at join time
    const [micIssue, setMicIssue] = useState(null); // { reason: string } | null

    const rafRef = useRef(null);
    const noFaceCountdownRef = useRef(null);  // interval for client-side no-face countdown
    const lastGazeToastRef = useRef(0);        // epoch of last sideways-gaze soft toast
    const lastMultiFaceToastRef = useRef(0);   // epoch of last multiple-faces soft toast
    // Consecutive-frame counter for multiple-face detection.
    // Haar cascade has frequent single-frame false positives (background objects, reflections).
    // We require 3 consecutive 2.5 s polls (= 7.5 s) before showing any warning or logging.
    const consecutiveMultiFaceRef = useRef(0);
    const noFaceWarnedRef = useRef(false);    // prevent toast spam for no-face warning
    const videoRef = useRef(null);
    const audioRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const mediaStreamInitRef = useRef(false);
    const lastVisRef = useRef(Date.now());
    const pendingTabWarnRef = useRef(false);     // show modal when candidate returns
    const intentionalFSExitRef = useRef(false);  // we triggered fullscreen exit ourselves
    const fullscreenExitCountRef = useRef(0);     // track manual exits; terminate on 2nd
    const phaseRef = useRef('interview');         // always-current phase for async closures
    const terminatedRef = useRef(false);          // always-current terminated flag for proctoring loop
    const submittedQuestionsRef = useRef(new Set()); // guard against double-submit per question
    // Set to true once onInterviewComplete has been called — prevents error-catch from showing
    // a spurious "Failed to finalize" message if a duplicate last-question submit fails after
    // the real one already succeeded.
    const interviewFinishedRef = useRef(false);
    // Captures question data at recording-stop time so transcription callback can use it
    const pendingQuestionRef = useRef(null);
    // Always-current refs for async closures (avoid stale values in background jobs)
    const voiceEventsRef = useRef([]);
    const gazeInfoRef = useRef({ direction: 'unknown', on_screen_pct: 100 });
    const tabSwitchCountRef = useRef(0);
    // Refs for proctoring loop — the loop's setInterval callback captures these on creation
    // so we CANNOT use the state values directly (they'd be forever stale). Refs are
    // always current without adding voiceLevel/dualVoiceDetected to the dep array (which
    // would destroy + recreate the interval on every audio frame).
    const voiceLevelRef = useRef(0);
    const dualVoiceRef = useRef(false);

    const { level: voiceLevel, events: voiceEvents, dualVoiceDetected, dualFlagCountRef } =
        useVoiceMonitor(mediaStream, !terminated);

    // Keep refs in sync for use in async background closures
    voiceEventsRef.current = voiceEvents;
    gazeInfoRef.current = gazeInfo;
    tabSwitchCountRef.current = tabSwitchCount;
    phaseRef.current = phase;
    terminatedRef.current = terminated;
    voiceLevelRef.current = voiceLevel;
    dualVoiceRef.current = dualVoiceDetected;

    // ── Soft toast helper ──────────────────────────────────────────────────────
    const addSoftToast = useCallback((msg, type = 'info') => {
        const id = Date.now() + Math.random();
        setSoftToasts(prev => [...prev.slice(-2), { id, msg, type }]);
        setTimeout(() => setSoftToasts(prev => prev.filter(t => t.id !== id)), 7000);
    }, []);

    /* ── mic health watchdog (warn if silence while recording) ─────────────── */
    useEffect(() => {
        if (!sessionData?._id || terminated || phase !== 'interview') return;
        if (!recording) { setMicIssue(null); return; }

        let iv;
        let silentMs = 0;
        const SILENCE_LEVEL = 6;      // below this considered “no input”
        const SILENCE_WARN_MS = 10_000; // 10s sustained silence → warn

        iv = setInterval(() => {
            // voiceLevel is 0–100 (derived from analyser average). When it stays low
            // for a sustained period during recording, candidates often have muted/wrong mic.
            if (voiceLevelRef.current < SILENCE_LEVEL) {
                silentMs += 500;
            } else {
                silentMs = 0;
                if (micIssue) setMicIssue(null);
                return;
            }

            if (silentMs >= SILENCE_WARN_MS) {
                setMicIssue({ reason: 'No audio detected from your microphone.' });
            }
        }, 500);

        return () => clearInterval(iv);
    }, [sessionData?._id, terminated, phase, recording, micIssue]);

    /* ── device change watcher (earphones/bluetooth switching) ─────────────── */
    useEffect(() => {
        if (!navigator?.mediaDevices?.addEventListener) return;
        const onChange = () => {
            if (terminatedRef.current || phaseRef.current !== 'interview') return;
            // Device changes (AirPods connect/disconnect) often end audio tracks.
            setMicIssue({ reason: 'Audio device changed. If your mic stops working, refresh it.' });
            addSoftToast('Audio device changed — if your mic stops working, click “Refresh mic”.', 'warning');
        };
        navigator.mediaDevices.addEventListener('devicechange', onChange);
        return () => navigator.mediaDevices.removeEventListener('devicechange', onChange);
    }, [addSoftToast]);

    // Client-side proctoring — active only when backend service is degraded
    const clientProctor = useClientProctoring({
        videoRef,
        enabled: proctoringDegraded && !terminated,
    });

    // Merge backend + client-side proctoring values
    const effectiveFaceDetected = proctoringDegraded ? clientProctor.faceCount > 0 : faceDetected;
    const effectiveGazeInfo = proctoringDegraded
        ? { direction: clientProctor.gazeDirection, on_screen_pct: clientProctor.gazeOnScreenPct }
        : gazeInfo;

    const currentQuestion = sessionData?.questions[currentQuestionIndex];

    useEffect(() => { selectedMcqOptionRef.current = selectedMcqOption; }, [selectedMcqOption]);

    /* ── question transition ────────────────────────────────────────────── */
    useEffect(() => {
        setQuestionEntering(false);
        setRecording(false);
        const t = setTimeout(() => setQuestionEntering(true), 150);
        return () => clearTimeout(t);
    }, [currentQuestionIndex]);

    /* ── background submit helper ───────────────────────────────────────── */
    // Submits a response in background; if isLast, finalizes the interview.
    // Guards against duplicate calls for the same question (race between timer-expiry
    // and recording-stop paths) using submittedQuestionsRef.
    //
    // Design: the session is already marked 'completed' by submitResponse on the last
    // question. The finalizeInterview call generates the AI report — it is retried
    // silently on failure (never surfaces an error to the candidate). Even if all
    // retries fail, the admin's report page auto-generates the report on demand.
    const backgroundSubmit = useCallback((question, transcription, audioFileUrl, duration, isLast, mcqAnswer) => {
        const qid = question._id.toString();
        // Deduplication guard — skip if this question was already submitted
        if (submittedQuestionsRef.current.has(qid)) {
            console.warn('Duplicate submit prevented for question', qid);
            return;
        }
        submittedQuestionsRef.current.add(qid);

        setBgProcessing(true);
        const submitPromise = apiClient.submitResponse(sessionData._id, {
            questionId:     question._id,
            transcribedText: transcription,
            audioFileUrl,
            duration:        Math.max(0, duration),
            selectedOption:  mcqAnswer,
        });

        if (isLast) {
            submitPromise
                .then(async () => {
                    // Fetch the full tracking snapshot from the proctor service before
                    // generating the report so the integrity score has all signal data.
                    let proctoringTracking = {};
                    try {
                        const statusRes = await fetch(
                            `${API_CONFIG.BASE_URL}/proctor/session-status/${sessionData._id}`,
                            { signal: AbortSignal.timeout(5000) }
                        );
                        if (statusRes.ok) {
                            const statusData = await statusRes.json();
                            proctoringTracking = statusData.tracking || {};
                        }
                    } catch { /* proctor status fetch failure is non-fatal */ }

                    // Finalize with silent retries (up to 3 attempts, 4 s apart).
                    // Uses the candidate-accessible /finalize endpoint — no admin
                    // permission required. Never throws — errors are logged only.
                    const trackingPayload = {
                        voiceEvents:       voiceEventsRef.current,
                        gazeOnScreenPct:   gazeInfoRef.current.on_screen_pct ?? null,
                        tabSwitchCount:    tabSwitchCountRef.current,
                        dualAudioFlags:    dualFlagCountRef.current,
                        proctoringTracking,
                    };
                    const MAX_ATTEMPTS = 3;
                    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                        try {
                            await apiClient.finalizeInterview(sessionData._id, trackingPayload);
                            break; // succeeded
                        } catch (err) {
                            console.warn(`[Finalize] attempt ${attempt}/${MAX_ATTEMPTS} failed: ${err.message}`);
                            if (attempt < MAX_ATTEMPTS) {
                                // Exponential back-off: 4 s, then 8 s
                                await new Promise(r => setTimeout(r, 4000 * attempt));
                            }
                            // All retries exhausted — log only, admin page will auto-generate
                        }
                    }
                })
                .then(() => {
                    interviewFinishedRef.current = true;
                    document.exitFullscreen?.().catch(() => {});
                    onInterviewComplete();
                })
                .catch((err) => {
                    // submitResponse itself failed — retry silently without surfacing an error.
                    // The candidate stays on the finalizing screen and never sees a failure state.
                    if (interviewFinishedRef.current) return;
                    console.error('[Submit] Last question submit failed, retrying in 4 s:', err.message);
                    // Clear dedup guard so the retry can proceed
                    submittedQuestionsRef.current.delete(qid);
                    setTimeout(() => {
                        backgroundSubmit(question, transcription, audioFileUrl, duration, isLast, mcqAnswer);
                    }, 4000);
                })
                .finally(() => setBgProcessing(false));
        } else {
            submitPromise
                .catch(err => {
                    console.warn('[Submit] Mid-interview submit failed, retrying once:', err.message);
                    setTimeout(() => {
                        if (!submittedQuestionsRef.current.has(qid)) return;
                        apiClient.submitResponse(sessionData._id, {
                            questionId: question._id,
                            transcribedText: transcription,
                            audioFileUrl,
                            duration: Math.max(0, duration),
                            selectedOption: mcqAnswer,
                        }).catch(e => console.error('[Submit] Mid-interview retry also failed:', e.message));
                    }, 3000);
                })
                .finally(() => setBgProcessing(false));
        }
    }, [sessionData, onInterviewComplete]);

    /* ── advance to next question (client-side immediate) ───────────────── */
    const advanceOrFinalize = useCallback((questionIdx) => {
        const q = sessionData.questions[questionIdx];
        if (q) localStorage.removeItem(`timerEndTime-${sessionData._id}-${q._id}`);

        if (questionIdx + 1 >= sessionData.questions.length) {
            setPhase('finalizing'); // show finalizing screen immediately
        } else {
            setCurrentQuestionIndex(p => p + 1);
            setSelectedMcqOption(null); // clear MCQ selection for next question
            setError('');
        }
    }, [sessionData]);

    /* ── recording started — stable callback so AudioRecorder auto-start effect
     * doesn't keep resetting its 400 ms timer on every animation-frame re-render */
    const handleRecordingStart = useCallback(() => {
        setRecording(true);
        setSubmitConfirm(false);
    }, []); // no deps — only calls stable setters

    /* ── recording stopped — advance immediately, transcription in background */
    const handleRecordingStop = useCallback(() => {
        setRecording(false);
        if (!sessionData) return;
        const q = sessionData.questions[currentQuestionIndex];
        const isLast = currentQuestionIndex + 1 >= sessionData.questions.length;
        // Capture state for when transcription completes later. The MCQ selection must be
        // read here: advanceOrFinalize clears it, and transcription finishes long after.
        pendingQuestionRef.current = {
            question: q, displayTimeAtStop: displayTime, isLast, questionIdx: currentQuestionIndex,
            mcqAnswer: q?.questionFormat === 'mcq' ? selectedMcqOptionRef.current : undefined,
        };
        advanceOrFinalize(currentQuestionIndex);
    }, [sessionData, currentQuestionIndex, displayTime, advanceOrFinalize]);

    /* ── transcription done — fire background submit ────────────────────── */
    const handleTranscriptionComplete = useCallback((result) => {
        const pending = pendingQuestionRef.current;
        if (!pending) return;
        pendingQuestionRef.current = null;

        const { question, displayTimeAtStop, isLast, mcqAnswer } = pending;
        const transcription = result.isEmpty ? '[No speech detected]' : result.transcription;
        const audioFileUrl = result.isEmpty ? 'N/A' : result.audioFileUrl;
        const duration = question.timeLimitSeconds - Math.max(0, displayTimeAtStop);

        backgroundSubmit(question, transcription, audioFileUrl, duration, isLast, mcqAnswer);
    }, [backgroundSubmit]);

    /* ── time expired — no recording, submit directly ────────────────────── */
    const handleTimeExpired = useCallback((questionIdx) => {
        const q = sessionData?.questions[questionIdx];
        if (!q) return;
        // Stop recording if active
        if (audioRecorderRef.current?.isRecording()) {
            audioRecorderRef.current.stopRecording();
            // handleRecordingStop will fire → handleTranscriptionComplete handles submit
            return;
        }
        // Not recording: submit expired marker directly. Read the selection before
        // advanceOrFinalize clears it — a timed-out MCQ still counts if an option was picked.
        const isLast = questionIdx + 1 >= sessionData.questions.length;
        const mcqAnswer = q.questionFormat === 'mcq' ? selectedMcqOptionRef.current : undefined;
        advanceOrFinalize(questionIdx);
        backgroundSubmit(q, '[Time Expired]', 'N/A', q.timeLimitSeconds, isLast, mcqAnswer);
    }, [sessionData, advanceOrFinalize, backgroundSubmit]);

    /* ── coding question submit ─────────────────────────────────────────── */
    const handleCodeSubmit = useCallback((code) => {
        if (!sessionData) return;
        const q = sessionData.questions[currentQuestionIndex];
        const isLast = currentQuestionIndex + 1 >= sessionData.questions.length;
        const duration = q.timeLimitSeconds - Math.max(0, displayTime);
        if (q) localStorage.removeItem(`timerEndTime-${sessionData._id}-${q._id}`);
        advanceOrFinalize(currentQuestionIndex);
        backgroundSubmit(q, `[CODE]\nLanguage: ${q.language || 'javascript'}\n\n${code}`, 'N/A', duration, isLast);
    }, [sessionData, currentQuestionIndex, displayTime, advanceOrFinalize, backgroundSubmit]);

    /* ── timer ──────────────────────────────────────────────────────────── */
    useEffect(() => {
        if (!currentQuestion || !sessionData || phase !== 'interview') return;
        const key = `timerEndTime-${sessionData._id}-${currentQuestion._id}`;
        let end = localStorage.getItem(key);
        if (!end) { end = Date.now() + currentQuestion.timeLimitSeconds * 1000; localStorage.setItem(key, end); }
        setQuestionMaxTime(currentQuestion.timeLimitSeconds);
        setDisplayTime(Math.round((Number(end) - Date.now()) / 1000));
        const capturedIdx = currentQuestionIndex;
        const tick = () => {
            const rem = Math.max(0, Math.round((Number(end) - Date.now()) / 1000));
            setDisplayTime(rem);
            if (rem <= 0) { handleTimeExpired(capturedIdx); return; }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    }, [currentQuestion, sessionData, phase]);

    /* ── session load + mark in_progress ───────────────────────────────── */
    useEffect(() => {
        if (!uniqueLink) { setError('No interview link.'); setIsLoading(false); return; }
        apiClient.getSessionByLink(uniqueLink)
            .then(async r => {
                const session = r.data;
                setSessionData(session);
                setCurrentQuestionIndex(session.currentQuestionIndex || 0);

                // /start is the gated path for question delivery.
                // getSessionByLink returns questions:[] when status=scheduled to prevent
                // candidates from seeing questions before the interview begins (via DevTools).
                // We must call markSessionStarted and merge the returned questions in.
                // If the session is already in_progress (e.g. page refresh mid-interview),
                // getSessionByLink already returns the full questions, so this is non-fatal.
                try {
                    const startResult = await apiClient.markSessionStarted(session._id);
                    // startResult is the raw JSON: { success, status, questions }
                    if (startResult?.questions?.length) {
                        setSessionData(prev =>
                            prev ? { ...prev, questions: startResult.questions } : prev
                        );
                    }
                } catch {
                    /* non-fatal — questions already in sessionData when already in_progress */
                }
            })
            .catch(e => setError(e.message || 'Could not load session.'))
            .finally(() => setIsLoading(false));
    }, [uniqueLink]);

    /* ── abandon on tab/window close (keepalive beacon) ────────────────── */
    useEffect(() => {
        if (!sessionData?._id) return;
        const handleUnload = () => {
            // Use phaseRef (not the closed-over `phase`) so the check is always current
            // even if the effect hasn't re-run after setPhase('finalizing')
            if (phaseRef.current === 'finalizing') return;
            apiClient.abandonSessionBeacon(sessionData._id);
        };
        window.addEventListener('pagehide', handleUnload);
        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('pagehide', handleUnload);
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [sessionData]); // no longer needs `phase` in deps — uses phaseRef instead

    /* ── fullscreen ─────────────────────────────────────────────────────── */
    useEffect(() => {
        document.documentElement.requestFullscreen?.().catch(() => {});
    }, []);

    // Exit fullscreen intentionally only when terminated or finalizing
    useEffect(() => {
        if (terminated || phase === 'finalizing') {
            intentionalFSExitRef.current = true;
            document.exitFullscreen?.().catch(() => {});
        }
    }, [terminated, phase]);

    // Guard against user pressing Escape / exiting fullscreen manually.
    // 1st manual exit → warning modal + re-enter. 2nd manual exit → terminate immediately.
    useEffect(() => {
        const handleFSChange = () => {
            if (document.fullscreenElement) return; // entering fullscreen — ignore
            if (intentionalFSExitRef.current) { intentionalFSExitRef.current = false; return; }
            if (terminated || phase === 'finalizing') return;

            fullscreenExitCountRef.current += 1;

            if (fullscreenExitCountRef.current >= 2) {
                // 2nd exit — terminate immediately (no second chance)
                const reason = 'Terminated: repeated fullscreen exit.';
                setTerminated(true);
                setTerminationReason(reason);
                if (sessionData?._id) {
                    apiClient.markSessionCompletedOrTerminated(sessionData._id, {
                        terminated: true,
                        terminationReason: reason,
                        proctoringInfractions: [reason],
                        warningCount: 0,
                        tabSwitchCount: tabSwitchCountRef.current,
                        proctoringEventLog,
                    }).catch(() => {});
                }
                return;
            }

            // 1st exit — re-enter fullscreen and warn
            document.documentElement.requestFullscreen?.().catch(() => {});
            setSafetyWarning(
                'You attempted to exit fullscreen. Your interview must remain in fullscreen mode. ' +
                'Exiting fullscreen again will immediately and permanently terminate your interview.'
            );
        };
        document.addEventListener('fullscreenchange', handleFSChange);
        return () => document.removeEventListener('fullscreenchange', handleFSChange);
    }, [terminated, phase, sessionData, proctoringEventLog]);

    /* ── tab switch ─────────────────────────────────────────────────────── */
    useEffect(() => {
        if (!sessionData) return;
        const handle = () => {
            if (document.hidden) {
                // ── Candidate switched away ───────────────────────────────
                const now = Date.now();
                if (now - lastVisRef.current < 1000) return; // debounce rapid flickers
                lastVisRef.current = now;

                setTabSwitchCount(p => {
                    const n = p + 1;
                    if (n >= 2) {
                        // Second switch → terminate immediately when they come back (or now)
                        setTimeout(() => {
                            setTerminated(true);
                            setTerminationReason('Terminated: repeated tab/window switching.');
                            apiClient.markSessionCompletedOrTerminated(sessionData._id, {
                                terminated: true,
                                terminationReason: 'Terminated: repeated tab/window switching.',
                                proctoringInfractions: ['Terminated: repeated tab/window switching.'],
                                warningCount: 0,
                                tabSwitchCount: n,
                                proctoringEventLog,
                            }).catch(() => {});
                        }, 500);
                    } else {
                        // First switch — flag to show warning modal when they return
                        pendingTabWarnRef.current = true;
                    }
                    return n;
                });
            } else {
                // ── Candidate returned ────────────────────────────────────
                if (pendingTabWarnRef.current) {
                    pendingTabWarnRef.current = false;
                    setSafetyWarning(
                        'You switched away from the interview tab. This has been recorded. ' +
                        'Switching tabs again will immediately and permanently terminate your interview.'
                    );
                }
            }
        };
        document.addEventListener('visibilitychange', handle);
        return () => document.removeEventListener('visibilitychange', handle);
    }, [sessionData, proctoringEventLog]);

    /* ── keyboard locks ─────────────────────────────────────────────────── */
    useEffect(() => {
        const k = (e) => { if (e.ctrlKey && 'wtn'.includes(e.key)) e.preventDefault(); if (e.key === 'F11') e.preventDefault(); };
        const c = (e) => e.preventDefault();
        document.addEventListener('keydown', k); document.addEventListener('contextmenu', c);
        return () => { document.removeEventListener('keydown', k); document.removeEventListener('contextmenu', c); };
    }, []);

    /* ── camera ─────────────────────────────────────────────────────────── */
    // Use initialStream (live stream passed from ResourceCheck via router state)
    // if it's still active — eliminates the 20-30 s re-request delay entirely.
    // Falls back to getUserMedia when navigated to directly without resource check.
    const initialStreamRef = useRef(initialStream);

    const refreshMicStream = useCallback(async () => {
        // Re-acquire a fresh stream (especially fixes long-session ended tracks).
        // Keep it audio+video to avoid breaking self-view/proctor snapshots.
        const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        // stop previous tracks (if any)
        try { mediaStreamRef.current?.getTracks?.().forEach(t => t.stop()); } catch {}
        mediaStreamRef.current = s;
        setMediaStream(s);
        return s;
    }, []);
    useEffect(() => {
        if (mediaStreamInitRef.current) return;

        const initial = initialStreamRef.current;
        if (initial && initial.active) {
            // Re-use already-granted stream — instant, no permission dialog
            mediaStreamInitRef.current = true;
            mediaStreamRef.current = initial;
            setMediaStream(initial);
            return () => {
                mediaStreamInitRef.current = false;
                initial.getTracks().forEach(t => t.stop());
            };
        }

        // Fallback: request fresh stream
        navigator.mediaDevices.getUserMedia({ video: true, audio: true })
            .then(s => { mediaStreamInitRef.current = true; mediaStreamRef.current = s; setMediaStream(s); })
            .catch(() => setError('Camera/microphone access denied.'));
        return () => { mediaStreamInitRef.current = false; mediaStreamRef.current?.getTracks().forEach(t => t.stop()); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!mediaStream || !videoRef.current) return;
        const v = videoRef.current;

        // Always (re-)assign the stream — safe even if already the same object.
        v.srcObject = mediaStream;
        // Show the self-view immediately — don't wait for loadedmetadata or play().
        // The loadedmetadata event timing is unreliable on first mount (the event can
        // fire before the listener is attached, or get cleaned up on rapid re-renders).
        // Since mediaStream is already live at this point, srcObject assignment is
        // enough to start rendering frames; the browser handles the rest via autoPlay.
        setVideoHasPlayed(true);
        v.play().catch(() => {});
    // sessionData is included so the effect re-fires when the session loads and
    // the video element appears in the DOM for the first time (before sessionData
    // loads, `currentQuestion` is undefined and the main interview UI is not
    // rendered, so videoRef.current is null on the first run).
    }, [mediaStream, currentQuestionIndex, sessionData]);

    /* ── join-time image capture — 3 snapshots at 3s/8s/15s ───────────────── */
    useEffect(() => {
        if (!sessionData || !videoHasPlayed || joinImages.length > 0) return;
        const delays = [3000, 8000, 15000];
        const timers = delays.map((delay, idx) =>
            setTimeout(async () => {
                const video = videoRef.current;
                if (!video?.videoWidth || !video?.videoHeight) return;
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    // mirror image (undo the CSS scale-x-[-1] so stored image isn't mirrored)
                    const ctx = canvas.getContext('2d');
                    ctx.translate(canvas.width, 0);
                    ctx.scale(-1, 1);
                    ctx.drawImage(video, 0, 0);
                    canvas.toBlob(async (blob) => {
                        if (!blob) return;
                        try {
                            // sessionId + imageIndex go as URL query params so they are
                            // available inside multer's filename() callback (which fires
                            // before multipart text fields are fully parsed into req.body).
                            const qs = `?sessionId=${encodeURIComponent(sessionData._id)}&imageIndex=${idx + 1}`;
                            const formData = new FormData();
                            formData.append('image', blob, `join-${idx + 1}.jpg`);
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${API_CONFIG.BASE_URL}/interview/capture-image${qs}`, {
                                method: 'POST',
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                                body: formData,
                            });
                            if (res.ok) {
                                const data = await res.json();
                                setJoinImages(prev => [...prev, data.imageUrl]);
                            }
                        } catch { /* non-fatal — capture is best-effort */ }
                    }, 'image/jpeg', 0.82);
                } catch { /* non-fatal */ }
            }, delay)
        );
        return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionData?._id, videoHasPlayed]);

    /* ── per-question snapshot — 1 image captured 2s after each question ── */
    useEffect(() => {
        if (!sessionData || !videoRef.current || phase !== 'interview') return;
        const q = sessionData.questions[currentQuestionIndex];
        if (!q) return;
        const timer = setTimeout(async () => {
            const video = videoRef.current;
            if (!video?.videoWidth || !video?.videoHeight || terminated) return;
            try {
                const canvas = document.createElement('canvas');
                canvas.width  = video.videoWidth;
                canvas.height = video.videoHeight;
                // Mirror to match stored join images (undo CSS scale-x-[-1])
                const ctx = canvas.getContext('2d');
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
                ctx.drawImage(video, 0, 0);
                canvas.toBlob(async (blob) => {
                    if (!blob) return;
                    try {
                        const qs = [
                            `sessionId=${encodeURIComponent(sessionData._id)}`,
                            `questionIndex=${currentQuestionIndex}`,
                            `questionId=${encodeURIComponent(q._id)}`,
                            `imageIndex=q${currentQuestionIndex + 1}`,
                        ].join('&');
                        const formData = new FormData();
                        formData.append('image', blob, `q${currentQuestionIndex + 1}.jpg`);
                        const token = localStorage.getItem('token');
                        await fetch(`${API_CONFIG.BASE_URL}/interview/capture-image?${qs}`, {
                            method: 'POST',
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                            body: formData,
                        });
                    } catch { /* non-fatal */ }
                }, 'image/jpeg', 0.82);
            } catch { /* non-fatal */ }
        }, 2000); // 2 s after question appears
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestionIndex, sessionData?._id]);

    /* ── proctoring loop ────────────────────────────────────────────────── */
    useEffect(() => {
        let iv;
        if (mediaStream && videoRef.current && sessionData) {
            const PROCTOR_FRAME_INTERVAL_MS = 2500; // 2.5s reduces load + avoids 413/timeouts
            iv = setInterval(async () => {
                // ── Early-exit guards ───────────────────────────────────────
                // Bail immediately if the interview is already over so we don't
                // send frames after termination / during the finalizing screen.
                if (terminatedRef.current || phaseRef.current !== 'interview') return;
                if (!videoRef.current?.videoWidth) return;

                // Reduce payload size to avoid 413 at proxy (OpenResty/Nginx):
                // downscale frames and lower JPEG quality.
                const MAX_W = 640;
                const srcW = videoRef.current.videoWidth;
                const srcH = videoRef.current.videoHeight;
                const scale = srcW > MAX_W ? (MAX_W / srcW) : 1;
                const dstW = Math.max(1, Math.round(srcW * scale));
                const dstH = Math.max(1, Math.round(srcH * scale));
                const c = document.createElement('canvas');
                c.width = dstW;
                c.height = dstH;
                const ctx = c.getContext('2d');
                ctx.drawImage(videoRef.current, 0, 0, dstW, dstH);
                try {
                    const res = await fetch(`${API_CONFIG.BASE_URL}/proctor/proctor`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            image:               c.toDataURL('image/jpeg', 0.45),
                            session_id:          sessionData._id,
                            // Use refs (not state) — the interval closure captures values at
                            // creation time. State-based values would be forever stale (always
                            // the initial 0/false), meaning the Python backend would never see
                            // any audio activity and lip-sync detection would never fire.
                            audio_level:         voiceLevelRef.current,
                            dual_voice_detected: dualVoiceRef.current,
                        }),
                    });
                    if (!res.ok) { setProctoringDegraded(true); return; }
                    const r = await res.json();
                    // The proxy always returns HTTP 200, even when the proctor service
                    // is offline (it returns { service_degraded: true } in that case).
                    // We must check the flag explicitly rather than relying on res.ok.
                    setProctoringDegraded(!!r.service_degraded);
                    if (r.service_degraded) return; // skip all processing — no real data

                    // ── Face / gaze ─────────────────────────────────────────
                    const fc = r.face_count ?? 1;
                    setFaceCount(fc);
                    setFaceDetected(fc > 0);
                    if (r.gaze) setGazeInfo(r.gaze);

                    // ── No-face warning (40 s mark) ─────────────────────────
                    if (r.no_face_warning) {
                        setNoFaceWarning({ secsLeft: Math.round(r.secs_until_terminate ?? 20) });
                        // Start client-side countdown so it ticks between backend polls.
                        // The countdown auto-stops when it reaches 0 — the interval would
                        // otherwise run forever emitting { secsLeft: 0 } on every tick.
                        if (!noFaceCountdownRef.current) {
                            noFaceCountdownRef.current = setInterval(() => {
                                setNoFaceWarning(prev => {
                                    if (!prev) {
                                        clearInterval(noFaceCountdownRef.current);
                                        noFaceCountdownRef.current = null;
                                        return null;
                                    }
                                    const next = Math.max(0, prev.secsLeft - 1);
                                    if (next === 0) {
                                        // Countdown expired — stop ticking; backend termination
                                        // response will arrive on the next 2.5 s poll.
                                        clearInterval(noFaceCountdownRef.current);
                                        noFaceCountdownRef.current = null;
                                    }
                                    return { secsLeft: next };
                                });
                            }, 1000);
                        }
                    } else {
                        if (noFaceCountdownRef.current) { clearInterval(noFaceCountdownRef.current); noFaceCountdownRef.current = null; }
                        setNoFaceWarning(null);
                    }

                    // ── Multiple-faces: consecutive-frame gate ───────────────
                    // Haar cascade produces frequent single-frame false positives.
                    // Require 3 consecutive 2.5 s polls (= 7.5 s of sustained detection)
                    // before showing ANY UI feedback — filters out reflections, posters, etc.
                    // Note: consecutiveMultiFaceRef is a ref (no re-render on change), so we
                    // drive all UI through the confirmedMultiFace state variable instead.
                    if (r.multiple_faces_warning) {
                        consecutiveMultiFaceRef.current += 1;
                    } else {
                        consecutiveMultiFaceRef.current = 0;
                    }
                    // Update confirmed state — React batches this, no extra render cost
                    setConfirmedMultiFace(consecutiveMultiFaceRef.current >= 3);

                    if (consecutiveMultiFaceRef.current >= 3 &&
                        Date.now() - lastMultiFaceToastRef.current > 120_000) {
                        lastMultiFaceToastRef.current = Date.now();
                        addSoftToast('Multiple faces detected — this has been flagged in your integrity report.', 'warning');
                    }

                    // ── Sideways-gaze soft toast (throttled: once per 60 s) ─
                    const gazeDir = r.gaze?.direction;
                    if ((gazeDir === 'left' || gazeDir === 'right') &&
                        Date.now() - lastGazeToastRef.current > 60_000) {
                        lastGazeToastRef.current = Date.now();
                        addSoftToast('Looking sideways is recorded in your integrity report.', 'warning');
                    }
                    // Note: dual-audio is recorded silently in the integrity score.
                    // No toast is shown to the candidate — the sustained-frame gate in
                    // useVoiceMonitor already filters single-speaker harmonics/noise,
                    // and showing "second voice" creates unnecessary anxiety for honest candidates.

                    // ── Termination (no-face timeout from backend) ──────────
                    if (r.terminated) {
                        if (noFaceCountdownRef.current) { clearInterval(noFaceCountdownRef.current); noFaceCountdownRef.current = null; }
                        setTerminated(true);
                        setTerminationReason(r.termination_reason || 'Session terminated by proctoring.');
                    }

                    // ── Track event log for session persist ─────────────────
                    if (r.tracking) {
                        setProctoringEventLog(prev => [...prev.slice(-49), { time: Date.now(), ...r.tracking }]);
                    }
                } catch { setProctoringDegraded(true); }
            }, PROCTOR_FRAME_INTERVAL_MS);
        }
        return () => { if (iv) clearInterval(iv); };
    // voiceLevel and dualVoiceDetected are intentionally NOT in deps — they are read
    // via refs (voiceLevelRef / dualVoiceRef) so the interval never needs to restart.
    // Restarting on every audio frame would cause continuous interval churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaStream, sessionData, videoHasPlayed]);

    /* ── heartbeat — ping every 30 s while actively interviewing ──────────── */
    useEffect(() => {
        if (!sessionData?._id || terminated) return;
        const iv = setInterval(() => {
            if (phaseRef.current !== 'interview' || terminatedRef.current) return;
            apiClient.heartbeatSession(sessionData._id).catch(() => { /* non-fatal */ });
        }, 30_000);
        // Send an immediate heartbeat on mount so the session is never stale
        apiClient.heartbeatSession(sessionData._id).catch(() => {});
        return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionData?._id, terminated]);

    /* ── terminated cleanup ─────────────────────────────────────────────── */
    useEffect(() => {
        if (!terminated || !sessionData?._id) return;
        // Clear no-face countdown
        if (noFaceCountdownRef.current) { clearInterval(noFaceCountdownRef.current); noFaceCountdownRef.current = null; }
        const reason = terminationReason || 'Proctoring violation.';
        apiClient.markSessionCompletedOrTerminated(sessionData._id, {
            terminated: true,
            terminationReason: reason,
            proctoringInfractions: [reason],
            warningCount: 0,
            tabSwitchCount: tabSwitchCountRef.current,
            proctoringEventLog,
        }).catch(() => {});
        mediaStream?.getTracks().forEach(t => t.stop());
        setMediaStream(null);
    }, [terminated, sessionData, terminationReason, proctoringEventLog, mediaStream]);

    /* ── render guards ──────────────────────────────────────────────────── */
    if (isLoading) return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Preparing your interview…</p>
        </div>
    );
    if (error && !sessionData) {
        const isExpired = error.toLowerCase().includes('expired') || error.toLowerCase().includes('passed');
        return (
            <div className="flex min-h-screen items-center justify-center bg-background p-6">
                <div className={`flex max-w-sm flex-col items-center gap-4 rounded-2xl border px-6 py-8 text-center shadow-sm ${
                    isExpired
                        ? 'border-orange-500/30 bg-orange-500/5'
                        : 'border-destructive/30 bg-destructive/10'
                }`}>
                    {isExpired
                        ? <Clock className="h-10 w-10 text-orange-500" />
                        : <AlertTriangle className="h-10 w-10 text-destructive" />}
                    <div>
                        <p className={`text-base font-bold ${isExpired ? 'text-orange-700 dark:text-orange-400' : 'text-destructive'}`}>
                            {isExpired ? 'Interview link expired' : 'Unable to load interview'}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
                    </div>
                </div>
            </div>
        );
    }
    if (!sessionData) return (
        <div className="flex min-h-screen items-center justify-center bg-background">
            <p className="text-sm text-muted-foreground">Could not load session data.</p>
        </div>
    );
    if (terminated) return <TerminatedPage onRedirect={onInterviewComplete} />;

    // Finalizing screen — shown immediately after last question, while report generates in background.
    // Report generation retries silently — no error is ever surfaced to the candidate here.
    if (phase === 'finalizing') return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6">
            <div className="relative flex h-20 w-20 items-center justify-center">
                <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
                <div className="absolute inset-2 animate-spin rounded-full border-2 border-transparent border-t-primary" />
                <Brain className="h-8 w-8 text-primary" />
            </div>
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-black text-foreground">Interview Complete</h2>
                <p className="text-sm text-muted-foreground">AI is evaluating your responses and generating your report…</p>
                <p className="text-xs text-muted-foreground/60">This usually takes 10–30 seconds. Please do not close this tab.</p>
            </div>
        </div>
    );

    if (!currentQuestion) return (
        <div className="flex min-h-screen items-center justify-center bg-background">
            <p className="text-sm text-muted-foreground">Could not load question data.</p>
        </div>
    );

    const isLastQ = currentQuestionIndex + 1 >= sessionData.questions.length;

    return (
        <div className="flex min-h-screen flex-col bg-background font-sans">
            {/* ── Alert banners ────────────────────────────────────────── */}
            {/* No-face warning — sticky, auto-dismisses when face returns */}
            {noFaceWarning && <NoFaceWarningBanner secsLeft={noFaceWarning.secsLeft} />}

            {tabSwitchCount > 0 && (
                <div className="z-50 bg-destructive px-4 py-2 text-center text-sm font-bold text-destructive-foreground">
                    ⚠ Tab switch detected ({tabSwitchCount}/2) — next switch permanently terminates this interview
                </div>
            )}
            {/* Proctoring degraded state is intentionally not shown to the candidate.
                Tab switching and audio monitoring remain fully active regardless.
                The admin report shows service_degraded status if relevant. */}
            {bgProcessing && (
                <div className="z-30 bg-primary/10 border-b border-primary/20 px-4 py-1 text-center text-[10px] font-semibold text-primary">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                        Saving your answer in the background…
                    </span>
                </div>
            )}

            {micIssue && phase === 'interview' && !terminated && (
                <div className="z-30 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-center text-xs font-semibold text-amber-200">
                    <span className="mr-2">⚠ {micIssue.reason}</span>
                    <button
                        onClick={() => {
                            addSoftToast('Refreshing microphone…', 'info');
                            refreshMicStream().catch(() => {
                                addSoftToast('Could not refresh mic. Check permissions and try again.', 'danger');
                            });
                        }}
                        className="inline-flex items-center rounded-md border border-amber-500/30 bg-black/20 px-2 py-1 text-[11px] font-bold text-amber-100 hover:bg-black/30"
                        type="button"
                    >
                        Refresh mic
                    </button>
                </div>
            )}

            {/* ── Header ─────────────────────────────────────────────── */}
            <header className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5 shadow-sm">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Brain className="h-4 w-4 text-primary" />
                    </div>
                    <div className="leading-tight">
                        <p className="text-sm font-bold text-foreground">{sessionData.template?.title || 'AI Interview'}</p>
                        <p className="text-[10px] text-muted-foreground">{sessionData.candidate?.firstName} {sessionData.candidate?.lastName}</p>
                    </div>
                </div>

                <QuestionProgress total={sessionData.questions.length} current={currentQuestionIndex} />

                <div className="flex items-center gap-2">
                    {tabSwitchCount > 0 && (
                        <div className="flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5">
                            <Shield className="h-3 w-3 text-destructive" />
                            <span className="text-xs font-bold text-destructive">{tabSwitchCount}/2</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-[10px] font-semibold text-muted-foreground">LIVE</span>
                    </div>
                </div>
            </header>

            {/* ── Main split layout ───────────────────────────────────── */}
            <div className="flex flex-1 overflow-hidden">

                {/* Left — AI + Question + Answer ───────────────────────── */}
                <main className={cn('flex flex-1 flex-col', currentQuestion.questionType === 'coding' ? 'overflow-hidden' : 'overflow-y-auto')}>

                    {/* AI interviewer strip */}
                    <div className="border-b border-border bg-muted/30 px-6 py-4">
                        <div className="mx-auto flex max-w-2xl items-center gap-4">
                            <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-500',
                                bgProcessing ? 'border-primary bg-primary/10 animate-pulse' : 'border-emerald-500/40 bg-emerald-500/10')}>
                                <Bot className={cn('h-6 w-6', bgProcessing ? 'text-primary' : 'text-emerald-600 dark:text-emerald-400')} />
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">AI Interviewer</p>
                                <p className="text-sm font-medium text-foreground">
                                    {bgProcessing ? 'Processing your answer in the background…' : 'Listening — speak your answer clearly.'}
                                </p>
                            </div>
                            {currentQuestion.questionType !== 'coding' && (
                                <WaveBar level={voiceLevel} count={16} />
                            )}
                        </div>
                    </div>

                    {/* Question + answer area */}
                    {currentQuestion.questionType === 'coding' ? (
                        /* ── Full-width coding IDE layout ───────────────────── */
                        <div className="flex flex-1 flex-col overflow-hidden px-3 pb-3 pt-2 gap-2">
                            {/* Coding header: label + timer */}
                            <div className="flex items-center justify-between px-1">
                                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                    Coding — Q{currentQuestionIndex + 1}/{sessionData.questions.length}
                                </span>
                                <RingTimer value={displayTime} max={questionMaxTime} />
                            </div>
                            {/* IDE fills the remaining height */}
                            <div className="flex-1 min-h-0" style={{ minHeight: '520px' }}>
                                <CodingIDE
                                    question={currentQuestion.questionText}
                                    codeTemplate={currentQuestion.codeTemplate || ''}
                                    language={currentQuestion.language || 'python'}
                                    testCases={currentQuestion.testCases || []}
                                    constraints={currentQuestion.constraints || ''}
                                    hints={currentQuestion.hints || []}
                                    onCodeSubmit={handleCodeSubmit}
                                />
                            </div>
                        </div>
                    ) : (
                    <div className="flex flex-1 flex-col items-center justify-start gap-5 px-6 py-6">
                        <div className="w-full max-w-2xl space-y-5">

                            {/* Question card */}
                            <div className={cn('transition-all duration-400', questionEntering ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3')}>
                                <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                                    <div className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-primary to-primary/20" />
                                    <div className="px-5 py-4">
                                        <div className="mb-3 flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                                    Q{currentQuestionIndex + 1}
                                                </span>
                                                {currentQuestion.difficulty && (
                                                    <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize',
                                                        currentQuestion.difficulty === 'easy' ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400' :
                                                        currentQuestion.difficulty === 'hard' ? 'border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400' :
                                                        'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400')}>
                                                        {currentQuestion.difficulty}
                                                    </span>
                                                )}
                                                {currentQuestion.questionType && (
                                                    <span className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold capitalize text-primary">
                                                        {currentQuestion.questionType}
                                                    </span>
                                                )}
                                            </div>
                                            <RingTimer value={displayTime} max={questionMaxTime} />
                                        </div>
                                        <p className="text-base font-medium leading-relaxed text-foreground">
                                            {currentQuestion.questionText}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* MCQ options — shown when question format is mcq */}
                            {currentQuestion.questionFormat === 'mcq' && currentQuestion.options?.length > 0 && (
                                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                        Select your answer
                                    </p>
                                    {currentQuestion.options.map((opt, i) => {
                                        const label = ['A', 'B', 'C', 'D'][i] || String(i + 1);
                                        const isSelected = selectedMcqOption === opt.text;
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => setSelectedMcqOption(opt.text)}
                                                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                                                    isSelected
                                                        ? 'border-primary bg-primary/10 text-foreground font-medium'
                                                        : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-muted/40'
                                                }`}
                                            >
                                                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold transition-colors ${
                                                    isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground'
                                                }`}>
                                                    {label}
                                                </span>
                                                {opt.text}
                                            </button>
                                        );
                                    })}
                                    {selectedMcqOption && (
                                        <p className="pt-1 text-[11px] text-muted-foreground text-center">
                                            Option selected. Record your reasoning below, then submit.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Answer zone (non-coding) */}
                            {(
                                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Recording active</span>
                                        </div>
                                        <WaveBar level={voiceLevel} count={24} />
                                    </div>
                                    <AudioRecorder
                                        ref={audioRecorderRef}
                                        onTranscriptionComplete={handleTranscriptionComplete}
                                        stream={mediaStream}
                                        autoStart
                                        autoStartKey={currentQuestion?._id}
                                        onRecordingStart={handleRecordingStart}
                                        onRecordingStop={handleRecordingStop}
                                        onRequestStreamRefresh={refreshMicStream}
                                        sessionId={sessionData?._id}
                                        questionId={currentQuestion?._id}
                                    />
                                    {/* Submit Answer button — visible while recording or waiting to start */}
                                    {!submitConfirm ? (
                                        <Button
                                            size="lg"
                                            disabled={!recording}
                                            onClick={() => setSubmitConfirm(true)}
                                            className="w-full h-12 gap-2 text-base font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white border-0"
                                        >
                                            <CheckCircle2 className="h-5 w-5" />
                                            {recording ? 'Submit Answer' : 'Waiting for recording…'}
                                        </Button>
                                    ) : (
                                        /* Confirmation step — warns answer can't be retaken */
                                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
                                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 text-center leading-snug">
                                                ⚠ Are you sure? Once submitted you <strong>cannot</strong> re-record this answer.
                                            </p>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setSubmitConfirm(false)}
                                                    className="flex-1 border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => { setSubmitConfirm(false); audioRecorderRef.current?.stopRecording(); }}
                                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border-0 font-bold"
                                                >
                                                    <CheckCircle2 className="h-4 w-4 mr-1" />
                                                    Yes, Submit
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && <p className="text-center text-xs text-destructive">{error}</p>}
                        </div>
                    </div>
                    )} {/* end coding/non-coding ternary */}
                </main>

                {/* Right — Camera + Tracking sidebar ─────────────────────── */}
                <aside className="flex w-[22rem] flex-col border-l border-border bg-card/50 xl:w-[28rem] 2xl:w-[32rem]">

                    {/* Camera — large, prominent self-view */}
                    <div className="border-b border-border p-3">
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Self View</p>
                        <div className={cn('relative w-full overflow-hidden rounded-xl bg-muted transition-all duration-300',
                            'aspect-[4/3]', // taller than 16:9 for more face visibility
                            noFaceWarning
                                ? 'ring-2 ring-red-500 shadow-[0_0_16px_rgba(239,68,68,0.35)]'
                                : confirmedMultiFace
                                    ? 'ring-2 ring-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.30)]'
                                    : 'ring-1 ring-border')}>
                            <video ref={videoRef} autoPlay playsInline muted className="block h-full w-full object-cover scale-x-[-1]" />
                            {(!mediaStream || !videoHasPlayed) && (
                                <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            )}

                            {/* No-face overlay on self-view */}
                            {noFaceWarning && (
                                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl bg-red-950/50 border-2 border-red-500/60 backdrop-blur-[1px]">
                                    <AlertTriangle className="h-7 w-7 text-red-400 mb-1" />
                                    <p className="text-xs font-bold text-red-300 text-center px-2">Return to camera!</p>
                                    <p className="text-xl font-black text-red-400 tabular-nums mt-0.5">{noFaceWarning.secsLeft}s</p>
                                </div>
                            )}

                            {/* Multiple-faces info overlay on self-view (report only).
                                Shown only after 3 consecutive detections (7.5 s sustained)
                                to avoid false positives from posters/reflections. */}
                            {!noFaceWarning && confirmedMultiFace && (
                                <div className="absolute bottom-1.5 inset-x-1.5 rounded-md px-2 py-0.5 text-center text-[10px] font-semibold backdrop-blur-sm bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                    {faceCount} faces — tracked in report
                                </div>
                            )}

                            {/* Recording dot */}
                            <div className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-md bg-black/50 px-1.5 py-0.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                                <span className="text-[9px] font-bold text-white">REC</span>
                            </div>
                        </div>
                    </div>

                    {/* Live tracking panel */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Live Tracking</p>

                        {/* Face presence — only show multi-face label after 3+ consecutive detections
                            (7.5 s sustained) to avoid single-frame Haar cascade false positives. */}
                        <TrackRow icon={Eye}
                            label={!effectiveFaceDetected ? 'Face not detected' : confirmedMultiFace ? `${faceCount} faces (tracked)` : 'Face detected'}
                            status={effectiveFaceDetected && !confirmedMultiFace}
                            danger={!!noFaceWarning} />
                        {/* Gaze — informational only, candidate reads screen */}
                        <TrackRow icon={Eye}
                            label="Gaze on screen"
                            value={`${effectiveGazeInfo.on_screen_pct ?? 100}%`}
                            danger={false} />
                        {/* Voice */}
                        <TrackRow icon={Mic}
                            label={voiceLevel > 5 ? 'Voice detected' : 'Awaiting voice'}
                            status={voiceLevel > 5} />
                        <TrackRow icon={Volume2}
                            label={voiceLevel > 70 ? 'High noise!' : 'Audio level'}
                            value={`${voiceLevel}%`}
                            danger={voiceLevel > 70} />
                        {/* Dual voice is recorded silently in the integrity score.
                            Not shown in the live panel — honest candidates experience
                            false positives from room noise and would be needlessly alarmed. */}
                        {/* Tab switch */}
                        <TrackRow icon={tabSwitchCount > 0 ? WifiOff : Wifi}
                            label={tabSwitchCount > 0 ? `Tab switch ×${tabSwitchCount}` : 'Tab focus OK'}
                            danger={tabSwitchCount > 0} />

                        {/* Voice events */}
                        {voiceEvents.length > 0 && (
                            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
                                <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Audio Events</p>
                                {voiceEvents.slice(-3).map((ev, i) => (
                                    <p key={i} className="text-[10px] text-amber-700/80 dark:text-amber-400/80">
                                        {ev.type === 'high_noise' ? `⚠ High noise (${ev.level}%)` : '⚠ Prolonged silence'}
                                    </p>
                                ))}
                            </div>
                        )}

                        {/* Interview tips */}
                        <div className="rounded-lg border border-border bg-muted/20 p-2.5 space-y-1.5">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Tips</p>
                            {['Keep your face centered', 'Look at the camera', 'Speak clearly & steadily', 'Avoid background noise'].map(t => (
                                <p key={t} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary/50" />{t}
                                </p>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

            {/* ── Safety Warning Modal (tab switch / fullscreen exit) ───── */}
            {safetyWarning && (
                <SafetyWarningModal
                    message={safetyWarning}
                    onDismiss={() => {
                        setSafetyWarning(null);
                        // Re-enter fullscreen when the candidate acknowledges the warning.
                        // Tab switches cause the browser to exit fullscreen automatically,
                        // so we must re-request it here — not just on the exit event.
                        // The fullscreenchange handler ignores enter events so this won't
                        // increment the exit counter or show a duplicate warning.
                        if (!document.fullscreenElement) {
                            document.documentElement.requestFullscreen?.().catch(() => {});
                        }
                    }}
                />
            )}

            {/* ── Soft toast stack (non-blocking, dismissible) ──────────── */}
            {softToasts.length > 0 && (
                <div className="fixed left-1/2 top-20 z-[9998] -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm space-y-2 pointer-events-auto">
                    {softToasts.map(t => (
                        <SoftToast
                            key={t.id}
                            msg={t.msg}
                            type={t.type}
                            onDismiss={() => setSoftToasts(prev => prev.filter(x => x.id !== t.id))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default InterviewPage;
